import { useCallback, useEffect, useRef, useState } from 'react';

const RAMP = 0.12; // seconds (time-constant) — every level change glides, never jumps

/** One shared preview player with ultra-smooth gain: fade-in on play, fade-out on stop/switch, glide on level changes. */
export function useSmoothPlayer(level) {
  const r = useRef({ ctx: null, gain: null, audio: null, timer: null });
  const [playing, setPlaying] = useState('');
  const [loading, setLoading] = useState('');

  const ensure = () => {
    if (r.current.ctx) return r.current;
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const audio = new Audio();
    audio.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    ctx.createMediaElementSource(audio).connect(gain).connect(ctx.destination);
    Object.assign(r.current, { ctx, gain, audio });
    return r.current;
  };

  const glide = (to) => {
    const { ctx, gain } = r.current;
    if (!ctx) return;
    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.setTargetAtTime(to, ctx.currentTime, RAMP);
  };

  const stop = useCallback(() => {
    const p = r.current;
    if (!p.audio) return;
    glide(0);
    clearTimeout(p.timer);
    p.timer = setTimeout(() => p.audio.pause(), 600);
    setPlaying('');
  }, []);

  const play = useCallback(async (url) => {
    const p = ensure();
    if (playing === url) { stop(); return; }
    clearTimeout(p.timer);
    if (p.ctx.state === 'suspended') await p.ctx.resume();
    if (playing) { glide(0); await new Promise((ok) => setTimeout(ok, 350)); }
    setLoading(url);
    p.audio.src = url;
    try {
      await p.audio.play();
      glide(level);
      setPlaying(url);
    } finally { setLoading(''); }
  }, [playing, level, stop]);

  useEffect(() => { if (playing) glide(level); }, [level, playing]);
  useEffect(() => () => { const p = r.current; clearTimeout(p.timer); p.audio?.pause(); p.ctx?.close(); }, []);

  return { play, stop, playing, loading };
}
